import './PathExplorer.scss';

import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button as AntButton, Flex, Space, Tooltip } from 'antd';
import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import AppSearch from '../common/AppSearch';
import { Alert, Button, Col, ColProps, Modal } from 'react-bootstrap';
import AppLoader from '../common/AppLoader';
import { Datasource } from '../../models/Datasource';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { useToasts } from '../../contexts/toasts';
import { QueryDetails, VizClient } from '../../api/VizClient';
import { normalizePath } from '../../utils';
import { EventEmitter } from '../../utils/EventEmitter';
import IconButton from '../common/IconButton';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import MDEditor from '@uiw/react-md-editor';
import rehypeSanitize from 'rehype-sanitize';
import MarkdownEditor from './MarkdownEditor';
import { v4 as generateUploadId } from 'uuid';
import FileSaver from 'file-saver';

interface TreeElement {
  name: string;
  csvFilesPresent?: boolean;
  elements?: TreeElement[];
  isDir?: boolean;
  selected?: boolean;
}

type Props = {
  datasource?: Datasource;
  actions?: React.ReactNode;
  pathActions?: React.ReactNode;
  path?: string;
  onPathChange?: (path: string) => void;
  onCsvPathVisited?: (value?: boolean) => void;
  className?: string;
  searchCol?: ColProps['md'];
  onDeleteFile?: VoidFunction;
  onRefetchPath?: (path: string, containsDataset: boolean) => void;
}

function PathExplorer({ path = "", onPathChange, onCsvPathVisited, datasource, actions, pathActions, className, searchCol, onRefetchPath }: Props) {
  const [search, setSearch] = useState('');
  const [directoryTree, setDirectoryTree] = useState<TreeElement[]>();
  const [error, setError] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMarkdownModal, setShowMarkdownModal] = useState(false);
  const [markdownContent, setMarkdownContent] = useState<string>();
  const [markdownError, setMarkdownError] = useState(false);
  const [markdownEditMode, setMarkdownEditMode] = useState(false);
  const [markdownValue, setMarkdownValue] = useState<string>();
  const [validMarkdown, setValidMarkdown] = useState<boolean>();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const filteredTree = useMemo(() => directoryTree?.filter(el => el.name.toLowerCase().includes(search.toLowerCase())), [directoryTree, search]);
  const datasourceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasources"));
  const pathParts = useMemo(() => (path.at(-1) === "/" ? path.slice(0, -1) : path).split("/"), [path]);

  const getDirectoryTree = useCallback(async () => {
    const body = new QueryDetails(datasource);
    body.path = normalizePath(path || body.prefixPath || "");

    try {
      const { data } = await VizClient.browseDirectories(body);
      setDirectoryTree(data);
      return data;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message;
      const status = err.response?.status;
      setError(true);
      showToast('directories-error', message, `Browsing Error ${status ? `(${status})` : ''}`, 'danger');
      setDirectoryTree([]);
    }
  }, [datasource, showToast, path]);

  const refetchPath = useCallback((tree: TreeElement[]) => {
    const containsDataset = (tree || []).some(v => v.csvFilesPresent);
    onCsvPathVisited?.(containsDataset);
    onRefetchPath?.(path || "", containsDataset);
  }, [onRefetchPath, path, onCsvPathVisited]);

  const refreshTree = useCallback(async () => {
    setDirectoryTree(undefined);
    const newTree = await getDirectoryTree();
    if (newTree) refetchPath(newTree);
  }, [getDirectoryTree, refetchPath]);

  useEffect(() => {
    EventEmitter.subscribe('fileUploadCompleted', refreshTree);
    return () => EventEmitter.unsubscribe('fileUploadCompleted');
  }, [refreshTree]);

  useEffect(() => {
    setDirectoryTree(undefined);
    if (datasource) getDirectoryTree();
    // eslint-disable-next-line
  }, [datasource, path]);

  const handleChangeDirectory = useCallback((element: TreeElement) => () => {
    if (!element.isDir) return;
    setSearch('');
    // TODO no need to fix dir?
    // setPath(path => path.concat(fixDirName(element.name)));
    const newPath = pathParts.concat(element.name);
    onPathChange?.(newPath.join("/"));
    // onPathChange?.(path => path.concat(element.name));
    onCsvPathVisited?.(element.csvFilesPresent);
  }, [pathParts, onPathChange, onCsvPathVisited]);

  const handleNavigateBack = useCallback(() => {
    const newPath = pathParts.slice(0, -1);
    onPathChange?.(newPath.join("/"));
    // onPathChange?.(path => path.slice(0, -1));
  }, [pathParts, onPathChange]);

  const navigateToPart = useCallback((index: number) => () => {
    const newPath = pathParts.slice(0, index + 1);
    onPathChange?.(newPath.join("/"));
    // onPathChange?.(path => path.slice(0, index + 1));
  }, [pathParts, onPathChange]);

  const handleDeleteFile = useCallback((filePath: string) => () => {
    setSelectedFile(filePath);
    setShowDeleteModal(true);
  }, []);

  const openMarkdownPreview = useCallback((filePath: string) => () => {
    setSelectedFile(filePath);
    setMarkdownContent(undefined);
    setMarkdownError(false);
    setShowMarkdownModal(true);
    const body = new QueryDetails(datasource);
    body.path = filePath;
    body.prefixPath = path;
    VizClient.downloadFile(body)
      .then(
        ({ data }) => (data as Blob).text().then(content => setMarkdownContent(content)),
        (err) => {
          setMarkdownError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('file-download-error', message, `File Download Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [datasource, path, showToast]);

  const handleMarkdownModalClose = () => {
    setShowMarkdownModal(false);
    setMarkdownContent(undefined);
    setMarkdownError(false);
    setSelectedFile(undefined);
    setMarkdownEditMode(false);
    setValidMarkdown(undefined);
  };

  const handleDownloadMarkdown = useCallback(() => {
    if (!markdownContent) return;
    const blob = new Blob([markdownContent], { type: 'text/markdown' });
    FileSaver.saveAs(blob, selectedFile || "file.md");
  }, [markdownContent, selectedFile]);

  const closeDeleteModal = useCallback(() => {
    setSelectedFile(undefined);
    setShowDeleteModal(false);
  }, []);

  const handleActivateEditMode = useCallback(() => {
    setMarkdownEditMode(true);
    setValidMarkdown(undefined);
    setMarkdownValue(markdownContent || "");
  }, [markdownContent]);

  const handleEditModeClose = () => {
    setMarkdownEditMode(false);
    setValidMarkdown(undefined);
    setMarkdownValue(undefined);
  };

  const handleSubmitMarkdown = useCallback(() => {
    if (!selectedFile || !markdownValue) return;
    showLoader();
    const fileName = selectedFile || "file.md";
    const file = new File([markdownValue], fileName, { type: 'text/markdown' });
    const formData = new FormData();
    formData.append('upload_id', generateUploadId());
    formData.append('files', file, fileName);
    const body = new QueryDetails(datasource);
    formData.append('query_details', JSON.stringify({
      host: body.host,
      accessKey: body.accessKey,
      secretKey: body.secretKey,
      bucket: body.bucket,
      port: body.port,
      secure: body.secure,
      prefixPath: path || "",
      regionName: body.regionName,
    }));

    VizClient.uploadFile(formData)
      .then(
        () => {
          hideLoader();
          setMarkdownContent(markdownValue);
          handleMarkdownModalClose();
          showToast('file-upload-success', `File saved successfully`, 'File Upload Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('file-upload-error', message, `Upload Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [datasource, path, selectedFile, markdownValue, showLoader, hideLoader, showToast]);

  const onDeleteFile = useCallback(() => {
    showLoader();
    const body = new QueryDetails(datasource);
    body.path = selectedFile || "";
    body.prefixPath = path;
    VizClient.deleteFile(body)
      .then(
        () => {
          hideLoader();
          closeDeleteModal();
          showToast('file-delete-success', `File deleted successfully`, 'File Delete Success', 'success');
          EventEmitter.dispatch('fileUploadCompleted');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('file-delete-error', message, `File Delete Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [datasource, showToast, path, selectedFile, closeDeleteModal, showLoader, hideLoader]);

  const renderMarkdown = useCallback(() => {
    if (markdownError) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred retrieving the file content. Try again later.</span>
      </Alert>
    );

    if (!markdownContent) return <AppLoader className="py-5" justify="center" size={80} />

    return markdownEditMode
      ? (
        <div data-color-mode="light">
          <MarkdownEditor
            value={markdownValue}
            height="100%"
            onChange={setMarkdownValue}
            placeholder="Enter markdown here."
            onValidated={setValidMarkdown}
          />
        </div>
      )
      : (
        <div data-color-mode="light">
          <MDEditor.Markdown
            className="bg-transparent"
            source={markdownContent}
            rehypePlugins={[[rehypeSanitize]]}
          />
        </div>
      );
  }, [markdownError, markdownContent, markdownEditMode, markdownValue]);

  const renderAsyncContent = useCallback(() => {
    if (!datasource) return (
      <Flex align="center" justify="center" className="no-datasource">
        <FontAwesomeIcon icon={solid('faucet-drip')} size="2x" />
        <h5 className="mb-0 ms-3">Choose one {datasourceLabels.singular} to see details here.</h5>
      </Flex>
    );

    if (error) return (
      <Alert variant="danger" className="py-2 m-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred retrieving the location content. Try again later.</span>
      </Alert>
    );

    if (!filteredTree) return <AppLoader className="py-5" justify="center" size={80} />

    return (
      <div className="tree-wrapper">
        {filteredTree?.length === 0 && (
          <Alert variant="info" className="py-1 m-1">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">No elements found.</span>
          </Alert>
        )}
        {filteredTree?.map(el => (
          <Flex justify="space-between" align="center" key={el.name}>
            <div className={classNames("tree-element", { dir: el.isDir })} onClick={handleChangeDirectory(el)}>
              <FontAwesomeIcon icon={el.isDir ? solid('folder') : solid('file')} />
              <span className="ms-2">{el.name}</span>
              {el.csvFilesPresent && (
                <Tooltip placement="right" mouseEnterDelay={0.5} title={`${el.isDir ? "Folder contains" : "File represents"} tabular data`}>
                  <FontAwesomeIcon icon={solid('table')} className="ms-2" />
                </Tooltip>
              )}
            </div>
            {!el.isDir && (
              <Space size={8}>
                {el.name.endsWith('.md') && (
                  <IconButton type="button" className="p-1" tooltip={{ title: "View MD", mouseEnterDelay: 1 }} onClick={openMarkdownPreview(el.name)}>
                    <FontAwesomeIcon icon={solid('eye')} size="sm" />
                  </IconButton>
                )}
                <IconButton type="button" className="p-1" tooltip={{ title: "Delete File", mouseEnterDelay: 1 }} onClick={handleDeleteFile(el.name)}>
                  <FontAwesomeIcon icon={solid('trash')} size="sm" className="text-danger" />
                </IconButton>
              </Space>
            )}
          </Flex>
        ))}
      </div>
    )
  }, [datasource, datasourceLabels, error, filteredTree, handleChangeDirectory, handleDeleteFile, openMarkdownPreview]);

  return (
    <div className={classNames("path-explorer", className)}>
      <Flex className="explorer-toolbar" gap={8} align="center">
        <Col className="w-100">
          <Flex align="center" className={classNames("path-input border rounded font-smaller", { 'bg-light': pathParts.length === 0 })}>
            {pathParts.length > 1 && (
              <AntButton type="text" size="small" onClick={handleNavigateBack}>
                <FontAwesomeIcon icon={solid('arrow-left')} size="sm" />
              </AntButton>
            )}
            <Col className="path-container">
              {pathParts.map((part, index) => {
                let noTrailingSlashPart: React.ReactNode = part.replace('/', '');
                if (!noTrailingSlashPart && index === 0) noTrailingSlashPart = <FontAwesomeIcon icon={solid('home')} title="Home" />

                return (
                  <div className="d-inline-block" key={index}>
                    {index === pathParts.length - 1
                      ? <span className="px-1">{noTrailingSlashPart}</span>
                      : <AntButton type="text" size="small" className="fw-bold px-1" key={index} onClick={navigateToPart(index)}>{noTrailingSlashPart}</AntButton>
                    }
                    {index < pathParts.length - 1 && <span style={{ margin: "0 0.125rem" }}>/</span>}
                  </div>
                )
              })}
            </Col>
            {pathActions}
          </Flex>
        </Col>
        <Col md={searchCol}>
          <AppSearch value={search} onChange={setSearch} />
        </Col>
        {actions}
      </Flex>
      <div className="explorer-content">{renderAsyncContent()}</div>

      <Modal show={showDeleteModal} onHide={closeDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>Delete File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete file <b>{selectedFile}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button size="sm" variant="light" className="border" onClick={closeDeleteModal}>Cancel</Button>
          <Button size="sm" variant="danger" onClick={onDeleteFile}>Confirm</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showMarkdownModal} size="xl" scrollable onHide={handleMarkdownModalClose}>
        <Modal.Header closeButton>
          <Modal.Title className="h5 mb-0">{selectedFile}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderMarkdown()}</Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="light" size="sm" className="border px-3" onClick={handleMarkdownModalClose}>Close</Button>
          <Space size={8}>
            <Button size="sm" variant="light" className="border px-3" disabled={markdownContent === undefined} onClick={markdownEditMode ? handleEditModeClose : handleActivateEditMode}>
              <FontAwesomeIcon icon={markdownEditMode ? solid('times') : solid('pen')} />
              <span className="ms-2">{markdownEditMode ? "Cancel" : "Edit"}</span>
            </Button>
            {markdownEditMode
              ? (
                <Button size="sm" variant="success" disabled={!validMarkdown} onClick={handleSubmitMarkdown}>
                  <FontAwesomeIcon icon={solid('check')} />
                  <span className="ms-2">Confirm</span>
                </Button>
              )
              : (
                <Button size="sm" variant="primary" disabled={markdownContent === undefined} onClick={handleDownloadMarkdown}>
                  <FontAwesomeIcon icon={solid('download')} />
                  <span className="ms-2">Download</span>
                </Button>
              )}
          </Space>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default memo(PathExplorer);